import { useEffect } from 'react';
import { useReloadAfterDataFlush } from 'hooks/useReloadAfterDataFlush';
import { useResetOnLogout } from './useResetOnLogout';
import { subscribeSwConfigReady } from 'services/serviceWorker/swConfigReady';
import { useProjectId } from 'store/project';
import type { DataActions } from '../types';
import type { EntityCollectionActions } from './types';

/**
 * Binds a collection store to the mounted app, like `useDocumentationInitializer`:
 * follows the worker's events, opens the collection for the selected project
 * (closing it on project change and unmount), reloads when the worker's config
 * is ready again or its data was flushed, and resets on logout. The work is
 * done by the store's actions (`createEntityCollectionActions`).
 */
export function useEntityCollectionInitializer<TModel>(
  actions: DataActions<TModel> & EntityCollectionActions<TModel>
): void {
  const projectId = useProjectId();
  const { subscribeToChannelMessages, openCollection, requestReload, reset } = actions;

  useEffect(() => subscribeToChannelMessages(), [subscribeToChannelMessages]);
  useEffect(() => openCollection(), [openCollection, projectId]);
  useEffect(() => subscribeSwConfigReady(requestReload), [requestReload]);
  useReloadAfterDataFlush(requestReload);
  useResetOnLogout(reset);
}
